import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import { DataRoomUser } from '../../../shared/models';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getResponseData } from '../../../shared/utils/axios';

interface LenderDeleteDialogProps {
	dataRoomGuid: string | undefined;
	user: DataRoomUser | null;
	close: () => void;
}

export const LenderDeleteDialog: React.FC<LenderDeleteDialogProps> = ({ dataRoomGuid, user, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onDeleteConfirmation = () => {
		if (user !== null) deleteLender(user);
	};

	const { mutate: deleteLender } = useMutation<DataRoomUser, unknown, DataRoomUser>(
		userToDelete =>
			getResponseData(
				api.delete<DataRoomUser>(CerebroApiEndpoints.DataRoomUsers.Specific(dataRoomGuid, userToDelete.guid))
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				Promise.all([
					client.invalidateQueries(QueryKeys.DataRoomUsers.Root(dataRoomGuid)),
					client.invalidateQueries(QueryKeys.Milestones.Root(dataRoomGuid)),
					client.invalidateQueries(QueryKeys.DataRoomUsers.TeamMembersByRoom(dataRoomGuid)),
				]).finally(() => {
					snackbar.enqueueSnackbar('Lender Deleted.', { variant: 'success' });
					close();
				});
			},
			onError: () => {
				client.invalidateQueries(QueryKeys.DataRoomUsers.Root(dataRoomGuid));
				snackbar.enqueueSnackbar('There was a problem deleting the lender.', { variant: 'error' });
			},
		}
	);

	return (
		<Dialog open={user !== null} onClose={close}>
			<DialogTitle>Confirm Delete Lender</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to delete {user?.firstName} {user?.lastName} at {user?.bankName}?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onDeleteConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
